/**
 * Halloween patterns — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const halloweenPatternsPack: ToilePack = {
    slug: 'halloween-patterns',
    category: 'season',
    name: 'Halloween patterns',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'tossed', label: 'Tossed pumpkins', subject: 'Small pumpkins, bats and stars tossed evenly across the frame, no two touching'},
        {key: 'webs', label: 'Spider webs', subject: 'Corner spider webs and small spiders repeated in an even grid across the frame'},
        {key: 'candy', label: 'Candy corn', subject: 'Candy corn pieces in tidy diagonal rows across the frame'},
    ],
    styles: [
        {key: 'cute', label: 'Cute repeat', style: 'cute flat surface pattern, rounded motifs, even spacing, seamless repeat', palette: 'Pastel orange, lilac and cream with black', framing: 'The motif repeated across the whole frame with even spacing, tileable edges'},
        {key: 'vintage', label: 'Vintage repeat', style: 'vintage halloween paper, halftone print, slight grain, seamless repeat', palette: 'Orange, black and cream', framing: 'The motif repeated across the whole frame, tileable edges'},
        {key: 'gothic', label: 'Gothic repeat', style: 'dark gothic damask-style surface pattern, ornate, seamless repeat', palette: 'Black and deep purple with a little bone white', framing: 'Dense repeat across the whole frame, tileable edges, no borders'},
    ],
    directions: ['a black cat motif instead', 'in orange and black only', 'a larger repeat scale'],
    directionHint: 'try: a black cat motif instead',
    seo: {
        title: 'Halloween patterns: pumpkins, webs, candy corn',
        metaDescription: 'Nine Halloween seamless patterns — tossed pumpkins, spider webs, candy corn — for fabric or paper, in cute, vintage or gothic repeat.',
        h1: 'Halloween seamless patterns: pumpkins, webs, candy corn',
        tagline: 'Nine patterns, three October motifs, three repeat treatments each — tileable for fabric or paper.',
        intro: 'An October surface pattern scattered evenly: small pumpkins and bats, corner spider webs in a grid, candy corn in diagonal rows. Each renders as a cute rounded repeat, a vintage halftone print, or a dark gothic damask, tileable with no visible seam. Ask for a different motif, like a black cat, and the same tileable structure carries it.',
        useCases: [
            'a fabric or quilting design',
            'a printable gift wrap or paper source',
            'a phone or desktop background pattern',
            'a small-batch product packaging design',
        ],
        faq: [
            {q: 'Does the pattern tile without a visible seam?', a: 'Yes — every design is a seamless repeat with tileable edges, ready for fabric, paper or a screen background.'},
            {q: 'Can I get a different motif, like a black cat?', a: 'Yes — the direction field takes any October motif, and the same tileable structure renders it in all three treatments.'},
            {q: "What's the difference between cute, vintage and gothic?", a: 'Cute is a rounded pastel repeat, vintage has a halftone-print grain, and gothic is a dark ornate damask-style repeat.'},
            {q: 'What size does this print or export at?', a: 'The pattern is built to repeat, not a fixed sheet — scale it to whatever the fabric, paper or screen needs.'},
        ],
    },
}
